import { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Upload, FileDown, Lock } from 'lucide-react';
import { supabase, BudgetLine, Category } from '../lib/supabase';
import { getCurrentSchoolYear, getSchoolYearLabel } from '../utils/schoolYear';
import { PDFExporter, formatCurrency, formatCurrencyForPDF } from '../utils/pdfExport';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useFiscalYear } from '../hooks/useFiscalYear';
import { UpgradeModal } from './UpgradeModal';

export function BudgetForecastTab() {
  const { effectiveUserId } = useAuth();
  const { canAccessBudget, planTier, loading: accessLoading } = useSubscriptionAccess();
  const { startMonth, startDay, loading: fiscalYearLoading } = useFiscalYear();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [budgetLines, setBudgetLines] = useState<(BudgetLine & { category?: Category })[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedYear, setSelectedYear] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [newBudget, setNewBudget] = useState({
    category_id: '',
    planned_amount: '',
    notes: ''
  });

  useEffect(() => {
    if (!fiscalYearLoading && selectedYear === 0) {
      setSelectedYear(getCurrentSchoolYear(startMonth, startDay));
    }
  }, [fiscalYearLoading, startMonth, startDay]);

  useEffect(() => {
    if (selectedYear > 0) {
      loadCategories();
      loadBudgetLines();
    }
  }, [selectedYear, effectiveUserId]);

  async function loadCategories() {
    if (!effectiveUserId) return;

    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('user_id', effectiveUserId)
      .eq('is_internal_transfer', false)
      .order('name');

    if (!error && data) {
      setCategories(data);
    }
  }

  async function loadBudgetLines() {
    if (!effectiveUserId) return;

    const { data, error } = await supabase
      .from('budget_lines')
      .select('*, categories(*)')
      .eq('user_id', effectiveUserId)
      .eq('year', selectedYear)
      .order('planned_amount', { ascending: false });

    if (!error && data) {
      setBudgetLines(data.map(line => ({
        ...line,
        category: line.categories
      })));
    }
  }

  function downloadCSVTemplate() {
    const csvContent = `Catégorie;Montant;Notes
Cotisations;5000;Cotisations annuelles des adhérents
Subventions municipales;15000;Subvention de la mairie
Fournitures scolaires;3000;Achat de fournitures pour l'année
Activités périscolaires;8000;Organisation des activités extrascolaires
Assurance;1200;Assurance responsabilité civile
Électricité et chauffage;2500;Factures d'énergie annuelles`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'modele-import-previsionnel.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  async function createBudgetLine() {
    if (!effectiveUserId || !newBudget.category_id || !newBudget.planned_amount) return;

    const { error } = await supabase
      .from('budget_lines')
      .insert([{
        user_id: effectiveUserId,
        category_id: newBudget.category_id,
        year: selectedYear,
        planned_amount: parseFloat(newBudget.planned_amount),
        notes: newBudget.notes
      }]);

    if (!error) {
      loadBudgetLines();
      setNewBudget({ category_id: '', planned_amount: '', notes: '' });
      setShowAddModal(false);
    }
  }

  async function updateBudgetLine(id: string, planned_amount: number, notes: string) {
    if (!effectiveUserId) return;

    const { error } = await supabase
      .from('budget_lines')
      .update({ planned_amount, notes })
      .eq('id', id)
      .eq('user_id', effectiveUserId);

    if (!error) {
      loadBudgetLines();
    }
  }

  async function deleteBudgetLine(id: string) {
    if (!effectiveUserId) return;

    const { error } = await supabase
      .from('budget_lines')
      .delete()
      .eq('id', id)
      .eq('user_id', effectiveUserId);

    if (!error) {
      loadBudgetLines();
    }
  }

  async function handleBudgetImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());

    const budgetsToImport: { category_id: string; planned_amount: number; notes: string }[] = [];

    for (let i = 1; i < lines.length; i++) {
      const [categoryName, amount, notes] = lines[i].split(/[,;]/).map(s => s.trim());
      const category = categories.find(c => c.name === categoryName);

      if (category && amount) {
        budgetsToImport.push({
          category_id: category.id,
          planned_amount: parseFloat(amount),
          notes: notes || ''
        });
      }
    }

    if (budgetsToImport.length > 0 && effectiveUserId) {
      const budgetWithYear = budgetsToImport.map(b => ({ ...b, year: selectedYear, user_id: effectiveUserId }));
      const { error } = await supabase.from('budget_lines').insert(budgetWithYear);

      if (!error) {
        loadBudgetLines();
        setShowImportModal(false);
      }
    }
  }

  const totalIncome = budgetLines
    .filter(line => line.category?.type === 'income')
    .reduce((sum, line) => sum + Number(line.planned_amount), 0);

  const totalExpense = budgetLines
    .filter(line => line.category?.type === 'expense')
    .reduce((sum, line) => sum + Number(line.planned_amount), 0);

  const balance = totalIncome - Math.abs(totalExpense);

  const exportToPDF = () => {
    const pdf = new PDFExporter();

    pdf.getDoc().text('Budget Previsionnel', pdf['pageWidth'] / 2, pdf['currentY'], { align: 'center' });
    pdf['currentY'] += 10;

    pdf.addSection(`Annee scolaire ${getSchoolYearLabel(selectedYear)}`);

    pdf.addSummaryBox([
      { label: 'Total recettes', value: formatCurrencyForPDF(totalIncome), highlight: false },
      { label: 'Total depenses', value: formatCurrencyForPDF(totalExpense), highlight: false },
      { label: 'Solde', value: formatCurrencyForPDF(balance), highlight: true }
    ]);

    const incomeLines = budgetLines.filter(line => line.category?.type === 'income');
    if (incomeLines.length > 0) {
      pdf.addSection('Recettes previsionnelles');
      const incomeData = incomeLines.map(line => ({
        categorie: line.category?.name || 'Sans categorie',
        montant: formatCurrencyForPDF(Number(line.planned_amount)),
        notes: line.notes || '-'
      }));

      pdf.addTable(
        [
          { header: 'Categorie', dataKey: 'categorie' },
          { header: 'Montant prevu', dataKey: 'montant' },
          { header: 'Notes', dataKey: 'notes' }
        ],
        incomeData,
        {
          columnStyles: {
            0: { halign: 'left', cellWidth: 60 },
            1: { halign: 'right', cellWidth: 40 },
            2: { halign: 'left', cellWidth: 70 }
          }
        }
      );
    }

    const expenseLines = budgetLines.filter(line => line.category?.type === 'expense');
    if (expenseLines.length > 0) {
      pdf.addSection('Depenses previsionnelles');
      const expenseData = expenseLines.map(line => ({
        categorie: line.category?.name || 'Sans categorie',
        montant: formatCurrencyForPDF(Number(line.planned_amount)),
        notes: line.notes || '-'
      }));

      pdf.addTable(
        [
          { header: 'Categorie', dataKey: 'categorie' },
          { header: 'Montant prevu', dataKey: 'montant' },
          { header: 'Notes', dataKey: 'notes' }
        ],
        expenseData,
        {
          columnStyles: {
            0: { halign: 'left', cellWidth: 60 },
            1: { halign: 'right', cellWidth: 40 },
            2: { halign: 'left', cellWidth: 70 }
          }
        }
      );
    }

    pdf.generate({
      title: 'Budget Previsionnel',
      subtitle: `Annee scolaire ${getSchoolYearLabel(selectedYear)}`,
      schoolYear: getSchoolYearLabel(selectedYear)
    });
  };

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (!canAccessBudget) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full py-12 px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={32} className="text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Vous voyez ce qui se passe aujourd'hui
            </h2>
            <p className="text-gray-600 mb-6">
              Le budget et les scénarios servent à comprendre ce qui va se passer.
              Passez à l'offre <strong>Anticipation</strong> pour projeter votre trésorerie et tester vos décisions.
            </p>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Découvrir l'offre Anticipation
            </button>
          </div>
        </div>

        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentPlan={planTier}
          targetPlan="anticipation"
          blockedFeature="budget"
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Prévisionnel budgétaire</h2>
          <div className="flex items-center gap-4 mt-2">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[2023, 2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{getSchoolYearLabel(year)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportToPDF}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FileDown size={18} />
            Exporter PDF
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Upload size={20} />
            Importer prévisionnel
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Ajouter une ligne
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Recettes prévues</p>
          <p className="text-2xl font-bold text-green-600">{totalIncome.toFixed(2)} €</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Dépenses prévues</p>
          <p className="text-2xl font-bold text-red-600">{Math.abs(totalExpense).toFixed(2)} €</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Solde prévisionnel</p>
          <p className="text-2xl font-bold" style={{ color: balance >= 0 ? '#059669' : '#dc2626' }}>
            {balance.toFixed(2)} €
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-green-50 px-6 py-3 border-b border-green-100">
            <h3 className="font-semibold text-green-800">Recettes</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {budgetLines
              .filter(line => line.category?.type === 'income')
              .map(line => (
                <BudgetLineRow
                  key={line.id}
                  line={line}
                  onUpdate={updateBudgetLine}
                  onDelete={deleteBudgetLine}
                />
              ))}
            {budgetLines.filter(line => line.category?.type === 'income').length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500">
                Aucune recette prévue
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-red-50 px-6 py-3 border-b border-red-100">
            <h3 className="font-semibold text-red-800">Dépenses</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {budgetLines
              .filter(line => line.category?.type === 'expense')
              .map(line => (
                <BudgetLineRow
                  key={line.id}
                  line={line}
                  onUpdate={updateBudgetLine}
                  onDelete={deleteBudgetLine}
                />
              ))}
            {budgetLines.filter(line => line.category?.type === 'expense').length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500">
                Aucune dépense prévue
              </div>
            )}
          </div>
        </div>
      </div>

      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Importer un prévisionnel CSV</h3>
            <div className="mb-4 space-y-2">
              <p className="text-sm text-gray-600">
                Le fichier CSV doit contenir les colonnes suivantes :
              </p>
              <ul className="text-sm text-gray-600 list-disc list-inside ml-2">
                <li><strong>Catégorie, Montant, Notes</strong></li>
                <li className="text-xs">Les catégories doivent déjà exister dans l'application</li>
              </ul>
              <button
                onClick={downloadCSVTemplate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <FileDown size={16} />
                Télécharger le modèle CSV
              </button>
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={handleBudgetImport}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Ajouter une ligne budgétaire</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                <select
                  value={newBudget.category_id}
                  onChange={(e) => setNewBudget({ ...newBudget, category_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} ({cat.type === 'income' ? 'Recette' : 'Dépense'})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Montant prévu (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newBudget.planned_amount}
                  onChange={(e) => setNewBudget({ ...newBudget, planned_amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={newBudget.notes}
                  onChange={(e) => setNewBudget({ ...newBudget, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Notes optionnelles..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewBudget({ category_id: '', planned_amount: '', notes: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createBudgetLine}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MODULE_BADGE: Record<string, { label: string; color: string }> = {
  pme: { label: 'PME', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  agri: { label: 'Agriculture', color: 'bg-green-100 text-green-700 border-green-200' },
};

function BudgetLineRow({
  line,
  onUpdate,
  onDelete
}: {
  line: BudgetLine & { category?: Category };
  onUpdate: (id: string, amount: number, notes: string) => void;
  onDelete: (id: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [amount, setAmount] = useState(line.planned_amount.toString());
  const [notes, setNotes] = useState(line.notes);

  const isAutoImported = Boolean(line.source_module);
  const badge = line.source_module ? MODULE_BADGE[line.source_module] : null;

  function handleSave() {
    onUpdate(line.id, parseFloat(amount), notes);
    setIsEditing(false);
  }

  if (isEditing && !isAutoImported) {
    return (
      <div className="px-6 py-4 space-y-2">
        <div className="font-medium text-gray-900">{line.category?.name}</div>
        <input
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes..."
          className="w-full px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setIsEditing(false)}
            className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            <Save size={14} />
            Sauvegarder
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`px-6 py-4 flex items-center justify-between ${isAutoImported ? 'bg-gray-50/50' : 'hover:bg-gray-50 cursor-pointer'}`}
      onClick={() => !isAutoImported && setIsEditing(true)}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div>
          <div className="font-medium text-gray-900">{line.category?.name}</div>
          {line.notes && !isAutoImported && <div className="text-sm text-gray-500">{line.notes}</div>}
          {isAutoImported && (
            <div className="text-xs text-gray-400 mt-0.5">Synchronisé automatiquement</div>
          )}
        </div>
        {badge && (
          <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full border ${badge.color}`}>
            {badge.label}
          </span>
        )}
      </div>
      <div className="flex items-center gap-4">
        <div className="text-lg font-semibold" style={{ color: line.category?.type === 'income' ? '#059669' : '#dc2626' }}>
          {Number(line.planned_amount).toFixed(2)} €
        </div>
        {!isAutoImported && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(line.id);
            }}
            className="text-red-600 hover:text-red-800"
          >
            <Trash2 size={16} />
          </button>
        )}
        {isAutoImported && (
          <div className="w-6 h-6" />
        )}
      </div>
    </div>
  );
}
